import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, EChatMessageStatus, EChatMessageType } from '../../data_models/chat';
import { EUserType } from '../../data_models/enums';
import { IFreelancer, RestrictedFreelancerRole } from '../../data_models/freelancer';
import { IEmployer } from '../../data_models/employer';
import useChat from '../../hooks/useChat';
import useRouting from '../../hooks/useRouting';
import TextInputField from '../common/TextInputField';
import { FilledButton, FlatButton, OutlinedButton } from '../common/Button';
import { UserId } from '../../data_models/helpers';
import useDisplayNotifications from '../../hooks/useDisplayNotifications';
import { DisplayNotificationType } from '../../models/DisplayNotification';
import { IoIosArrowDown, IoIosSend } from "react-icons/io";
import { EInvitationStatus, Invitation } from '../../data_models/invitation';
import { TiUserAdd } from "react-icons/ti";
import SelectTaskForInviteLightbox from '../lightboxes/projects/SelectTaskForInviteLightbox';
import useCurrentUser from '../../hooks/useCurrentUser';
import { IoIosAttach } from "react-icons/io";
import { MdCancel } from "react-icons/md";


import { useFilePicker } from 'use-file-picker';
import { fileSizeReadable } from '../../utils/fileUtils';

import SmallAvatar from '../common/SmallAvatar';
import { UserEnumTextMapper } from '../../data_models/enumToTextMapper';
import { getEmployerName } from '../../utils/employerUtils';
import { getFreelancerName } from '../../utils/freelancerUtils';
import { DEFAULT_USER_PROFILE } from '../../config/defaultAssetUrls';
import MessageTextComponent from './MessageTextComponent';
import MessageInvitationComponent from './MessageInvitationComponent';
import MessageFileComponent from './MessageFileComponent';
import MessageTextAndFileComponent from './MessageTextAndFileComponent';
import { InviteForTasksButton } from '../common/GhButtons';
import IconButton from '../common/IconButton';




const Messages: React.FC = ({ }) => {

    const { activeChatPartner,
        activeMessages,
        activeConversation,
        sendPrivateMessage,
        sendPrivateMessageFile,
        notifyTyping,
    } = useChat();
    const { currentUser, currentUserType } = useCurrentUser();

    const {
        activeChatHasOlderMessages,
        fetchOlderMessages,
    } = useChat();


    const { openFilePicker, filesContent, plainFiles, loading } = useFilePicker({
        accept: ['*'], // Accept all files
        multiple: false,
    });


    const { goToUserProfile } = useRouting();
    const { addNotification } = useDisplayNotifications();

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [fileToSend, setFileToSend] = useState<File>();
    const [fileInfoText, setFileInfoText] = useState('');

    const [chatPartnerType, setChatPartnerType] = useState<EUserType | undefined>();
    const [showSelectTaskForInviteLightbox, setShowSelectTaskForInviteLightbox] = useState(false);

    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const [scrollToAnchor, setScrollToAnchor] = useState(false);
    const [olderMessagesScrollAnchor, setOlderMessagesScrollAnchor] = useState<string | undefined>();

    const [sendFileNotification, setSendFileNotification] = useState<string | undefined>();


    const scrollDown = () => {
        if (olderMessagesScrollAnchor && scrollToAnchor) {
            // scroll to the anchor
            const anchor = document.getElementById(olderMessagesScrollAnchor);
            if (anchor) {
                anchor.scrollIntoView();
            }
            setScrollToAnchor(false);
            setOlderMessagesScrollAnchor(undefined);
        } else {
            // scroll to the bottom of the messages
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        // Handle the selected file after the file picker is closed
        if (filesContent.length > 0) {
            // const file = filesContent[0];
            const plain = plainFiles[0];
            setFileToSend(plain);

            const filename = plain.name;
            const filesize = plain.size;
            setFileInfoText(`Send File: ${filename} (${fileSizeReadable(filesize)})`);

        }
    }, [filesContent]);


    useEffect(() => {
        setNewMessage("")
    }, [activeChatPartner]);

    useEffect(() => {
        scrollDown();
    }, [activeMessages]);


    useEffect(() => {
        if (activeChatPartner) {
            setChatPartnerType('employerName' in activeChatPartner
                ? EUserType.EMPLOYER
                : EUserType.FREELANCER);

        } else {
            setChatPartnerType(undefined);
        }
    }, [activeChatPartner])

    const isEmployer = (): boolean => {
        return activeChatPartner ? 'employerName' in activeChatPartner : false;
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent the default action (like a form submission)
            handleSendPrivateMessage(); // Call the send message function
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewMessage(event.target.value);
        if (newMessage.trim() !== '') {
            notifyTyping();
        }
    }

    const handleSendPrivateMessage = async () => {
        if (activeChatPartner === undefined || chatPartnerType === undefined) return;
        if (newMessage.trim() === '') return;

        const userId: UserId = { id: activeChatPartner._id!, userType: chatPartnerType }

        sendPrivateMessage(userId, newMessage);
        setNewMessage('');
    }

    const clearSendFile = () => {
        setFileToSend(undefined);
        setFileInfoText('');
    }


    const handleSendFile = async () => {

        if (!fileToSend || activeChatPartner === undefined) {
            return;
        }
        if (!activeChatPartner || !chatPartnerType) {
            return;
        }
        const userId: UserId = { id: activeChatPartner._id!, userType: chatPartnerType }

        try {
            setSendFileNotification(`Sending ${fileToSend.name}...`);
            sendPrivateMessageFile(userId, fileToSend!).then(() => {
                setSendFileNotification(undefined);
            });
            clearSendFile()
        } catch (error: any) {
            addNotification({
                type: DisplayNotificationType.ERROR,
                message: error.message
            })
        }

        // sendPrivateMessage(userId, newMessage);
        // setNewMessage('');
    }

    const onFetchOlderMessages = async () => {
        const id = activeMessages ? activeMessages[0]._id : undefined;
        if (id) {
            setScrollToAnchor(true);
            setOlderMessagesScrollAnchor(id);
        }
        setLoadingOlderMessages(true);
        await fetchOlderMessages();
        setLoadingOlderMessages(false);
    }

    const canInviteActiveChatPartnerForTask = (): boolean => {
        // must be project manager and the chat partner must be freelancer

        if (currentUserType !== EUserType.FREELANCER) {

            return false;
        }
        if (!(currentUser as IFreelancer).restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)) {

            return false;
        }
        if (chatPartnerType !== EUserType.FREELANCER) {

            return false;
        }
        return true;
    }

    return (
        <div className="flex flex-col h-full">
            {/* Chat Partner Section */}
            <div className='relative top-0 left-0 right-0'>
                {activeChatPartner && (
                    <div>

                        <div className='p-2 border-b border-neutral-300 flex flex-row justify-between'
                        >
                            {/* Chat Partner Info */}
                            <div className='flex flex-row gap-2 cursor-pointer hover:underline'
                                onClick={() => {
                                    chatPartnerType && activeChatPartner && goToUserProfile(chatPartnerType, activeChatPartner._id!);
                                }}>
                                <SmallAvatar
                                    imageUrl={isEmployer()
                                        ? activeChatPartner.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_EMPLOYER_PROFILE_PICTURE
                                        : activeChatPartner.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE
                                    }
                                />
                                <div className='flex flex-col'>
                                    <p className='caption2 font-bold'>{isEmployer()
                                        ? getEmployerName(activeChatPartner as IEmployer)
                                        : getFreelancerName(activeChatPartner as IFreelancer)}</p>
                                    <div className='flex flex-wrap gap-x-2'>{!isEmployer() && (activeChatPartner as IFreelancer).roles &&
                                        (activeChatPartner as IFreelancer).roles!.map((role, index) =>
                                            <div key={role} className='flex items-center gap-1'>
                                                <p className='caption2'>{UserEnumTextMapper.freelancerRole(role)}</p>
                                                {index < ((activeChatPartner as IFreelancer).roles!.length - 1) && <div className='w-1 h-1 rounded-full bg-neutral-400' />}
                                            </div>
                                        )}
                                    </div>
                                </div>


                            </div>
                            {canInviteActiveChatPartnerForTask() && <InviteForTasksButton
                                freelancer={activeChatPartner as IFreelancer}
                            />}
                        </div>

                    </div>
                )}
            </div>

            {/* Messages Section */}
            <div className='flex-1 overflow-y-auto'>
                {/* If no messages are there - standard text */}
                {!activeMessages &&
                    chatPartnerType !== undefined &&
                    activeChatPartner && <p className='text-center mt-4'>Start a new conversation {chatPartnerType === EUserType.FREELANCER && (activeChatPartner as IFreelancer).firstName
                        ? `with ${(activeChatPartner as IFreelancer).firstName}`
                        : `with ${(activeChatPartner as IEmployer).employerName}`} </p>}
                {!activeMessages &&
                    chatPartnerType === undefined && <p className='text-center mt-4'>Start a new conversation</p>}

                {/* Messages */}
                {activeMessages &&
                    <div className='w-full h-full'>
                        {/* Load older messages Button */}
                        {activeChatHasOlderMessages && <div className='w-full my-2 flex justify-center'><FlatButton
                            onClick={onFetchOlderMessages}
                            isLoading={loadingOlderMessages}
                        >
                            Load older messages
                        </FlatButton></div>}

                        {/* Messages themselves */}
                        {activeMessages.map((message, idx) => (
                            <div id={message._id} key={message._id}
                                className="p-1"
                            >
                                {message.type === EChatMessageType.TEXT
                                    && <MessageTextComponent chatMessage={message} />}
                                {message.type === EChatMessageType.FILE
                                    && <MessageFileComponent chatMessage={message} />}
                                {message.type === EChatMessageType.TEXT_AND_FILE
                                    && <MessageTextAndFileComponent chatMessage={message} />}
                                {message.type === EChatMessageType.INVITATION && message.status !== EChatMessageStatus.WITHDRAWN
                                    && <MessageInvitationComponent message={message} />}

                            </div>
                        ))}

                        <div ref={messagesEndRef} />
                    </div>
                }
            </div>

            {/* Input field and action buttons */}
            {
                <div className='p-2'>
                    {sendFileNotification &&
                        <div className='flex flex-row gap-2 items-center'>
                            <div className='w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin' />
                            <p className='caption2'>{sendFileNotification}</p>
                        </div>}
                    <div className=" min-h-12 flex items-center space-x-4">
                        <div className='w-full'>
                            {fileInfoText && fileInfoText.length > 0
                                ? <div><p className='text-sm text-text3'>{fileInfoText}</p></div>
                                : <TextInputField
                                    value={newMessage}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type your message..."
                                />}
                        </div>

                        <IconButton
                            onClick={fileToSend ? handleSendFile : handleSendPrivateMessage}
                            icon={<IoIosSend />}
                            disabled={activeChatPartner === undefined}
                            theme='primary'
                        />

                        <IconButton
                            onClick={openFilePicker}
                            icon={<IoIosAttach />}
                            isLoading={loading}
                            disabled={activeChatPartner === undefined || sendFileNotification !== undefined}
                        />

                        {fileToSend && <IconButton
                            onClick={clearSendFile}
                            icon={<MdCancel />}
                            theme='danger'
                            disabled={activeChatPartner === undefined}
                        />}


                    </div>

                </div>}

            {showSelectTaskForInviteLightbox && <SelectTaskForInviteLightbox
                onClose={() => { setShowSelectTaskForInviteLightbox(false) }}
                freelancer={activeChatPartner as IFreelancer}
            />}
        </div>
    );

};

export default Messages;